/**
 * Account modal. Register upgrades the *current guest token* in place — same player row,
 * same stash — so the copy has to make clear nothing is lost. Login swaps to a different
 * account entirely, which is why the two are separate modes rather than one form.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import { session } from '../../net/session.js';
import { type HubController, useEscape, useSession } from './common.js';

export interface AuthProps {
  ctl: HubController;
  onClose: () => void;
}

type Mode = 'register' | 'login';

export function Auth(props: AuthProps): VNode {
  const auth = useSession();
  const [mode, setMode] = useState<Mode>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  useEscape(props.ctl, true, props.onClose);

  const valid = email.includes('@') && email.length >= 5 && password.length >= 8;

  const submit = (): void => {
    if (!valid || auth.busy) return;
    const run =
      mode === 'register' ? session.register(email, password) : session.login(email, password);
    // `session` already stores the reason on `.error`; the throw is for callers that want
    // to branch, and swallowing it here is what keeps a wrong password out of the console.
    run.then(
      () => {
        props.onClose();
      },
      () => {
        /* error is rendered from session state */
      },
    );
  };

  return (
    <div class="hub-scrim" onClick={props.onClose}>
      <div
        class="hub-modal hub-modal-narrow"
        role="dialog"
        aria-label="Account"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 class="hub-h2">{mode === 'register' ? 'Secure this account' : 'Sign in'}</h2>
        <p class="hub-notice">
          {mode === 'register'
            ? 'Your stash, Wick and progress stay exactly where they are — an email and a password just make them recoverable on another device.'
            : 'Signing in swaps to that account. Anything held by this guest stays with the guest.'}
        </p>

        <form
          class="hub-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label class="hub-field">
            <span class="hub-label">Email</span>
            <input
              class="hub-input"
              type="email"
              autocomplete="email"
              autocapitalize="off"
              spellcheck={false}
              value={email}
              onInput={(e) => {
                session.clearError();
                setEmail(e.currentTarget.value);
              }}
            />
          </label>

          <label class="hub-field">
            <span class="hub-label">Password</span>
            <input
              class="hub-input"
              type="password"
              autocomplete={mode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onInput={(e) => {
                session.clearError();
                setPassword(e.currentTarget.value);
              }}
            />
            <span class="hub-hint-line">At least 8 characters.</span>
          </label>

          {auth.error.length > 0 ? <p class="hub-error">{auth.error}</p> : null}

          <button
            type="submit"
            class="hub-btn hub-btn-amber hub-btn-wide"
            disabled={!valid || auth.busy}
          >
            {auth.busy ? 'Working…' : mode === 'register' ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <div class="hub-modal-acts">
          <button
            type="button"
            class="hub-btn hub-btn-quiet"
            onClick={() => {
              session.clearError();
              setMode(mode === 'register' ? 'login' : 'register');
            }}
          >
            {mode === 'register' ? 'I already have an account' : 'Create one instead'}
          </button>
          <button type="button" class="hub-btn hub-btn-quiet" onClick={props.onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
